import React from "react";
import { motion } from "framer-motion";
import BannerLogo from "../asset/Kesari-Glow-Logo-png.png";

const FlowerPetal = ({ rotate = 0, delay = 0 }) => (
  <motion.path
    d="M0,-15 C5,-12 10,-5 0,0 C-10,-5 -5,-12 0,-15"
    fill="rgba(255, 253, 240, 0.2)"
    opacity={0.15}
    transform={`rotate(${rotate})`}
    animate={{
      opacity: [0.1, 0.2, 0.1],
      scale: [1, 1.2, 1],
    }}
    transition={{
      duration: 3,
      delay: delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  />
);

const Flower = ({ x, y, size = 1, delay = 0 }) => (
  <motion.g
    initial={{ x, y }}
    animate={{
      y: [y, y + 100, y],
      x: [x, x + 30, x],
      rotate: [0, 360],
    }}
    transition={{
      duration: 20,
      delay,
      repeat: Infinity,
      ease: "linear",
    }}
  >
    <g transform={`scale(${size})`}>
      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
        <FlowerPetal key={i} rotate={angle} delay={i * 0.2} />
      ))}
      <motion.circle
        r="2"
        fill="rgba(255, 253, 240, 0.3)"
        opacity={0.2}
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.15, 0.3, 0.15],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </g>
  </motion.g>
);

export default function Banner() {
  const createFlowerPositions = () => {
    const positions = [];
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 6; j++) {
        positions.push({
          x: (j * 17) + Math.random() * 8,
          y: (i * 17) + Math.random() * 8,
          size: Math.random() * 0.4 + 0.3,
          delay: Math.random() * -15
        });
      }
    }
    return positions;
  };

  const flowerPositions = createFlowerPositions();

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-4 sm:px-6">
      {/* Premium Gradient Background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        className="absolute inset-0 bg-gradient-to-b from-purple900 via-purple800 to-purple600"
      />

      {/* Floating Flowers Background */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
          {flowerPositions.map((pos, i) => (
            <Flower
              key={i}
              x={pos.x}
              y={pos.y}
              size={pos.size}
              delay={pos.delay}
            />
          ))}
        </svg>
      </div>

      {/* Glowing Lines - Updated color */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-[1px] h-full bg-gradient-to-b from-transparent via-[rgba(255,253,240,0.2)] to-transparent"
            style={{
              left: `${(i + 1) * 10}%`,
            }}
            initial={{ y: '-100%' }}
            animate={{ y: '200%' }}
            transition={{
              duration: 15 + i * 2,
              repeat: Infinity,
              ease: "linear",
              delay: i * -2,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="relative mb-8 sm:mb-12 md:mb-16"
        >
          <motion.img
            src={BannerLogo}
            alt="Kesari Glow Logo"
            className="w-48 sm:w-56 md:w-64 lg:w-80 mx-auto"
            animate={{
              y: [0, -15, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.div>

        {/* Premium Taglines */}
        <motion.p
          className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-gold font-radon font-medium 
            max-w-3xl mx-auto mb-4 sm:mb-6 px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          Premium Cosmetics for Timeless Beauty
        </motion.p>

        <motion.p
          className="text-base sm:text-lg md:text-xl text-secondary/90 font-konnect 
            max-w-2xl mx-auto mb-8 sm:mb-12 px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          Discover our exclusive collection of luxury beauty products
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <a
            href="#products"
            className="w-full sm:w-auto bg-gradient-to-r from-gold to-secondary 
              px-8 py-3 sm:py-4 rounded-full text-purple900 font-radon 
              text-lg sm:text-xl font-medium tracking-wider
              transform hover:scale-105 transition-all duration-300 
              hover:from-secondary hover:to-gold
              min-w-[200px] sm:min-w-[220px] text-center"
          >
            Shop Collection
          </a>
          <a
            href="#about"
            className="w-full sm:w-auto bg-transparent border-2 border-gold/50 
              px-8 py-3 sm:py-4 rounded-full text-gold font-radon 
              text-lg sm:text-xl font-medium tracking-wider
              transform hover:scale-105 transition-all duration-300 
              hover:bg-gold/10 hover:border-gold
              min-w-[200px] sm:min-w-[220px] text-center"
          >
            Our Story
          </a>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
      >
        <motion.div
          className="w-6 h-10 border-2 border-gold/50 rounded-full p-1"
          animate={{ y: [0, 10, 0] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="w-1.5 h-3 bg-gold/50 rounded-full mx-auto" />
        </motion.div>
      </motion.div>
    </section>
  );
} 